import Bus from '../../bus'
import { BettorApi, OrderApi, SportsbookApi } from '../../api'

import { DeferredPromise, extractBonuses, getUID, logger } from '../../utils'

import BetProcessor from './BetProcessor'
import BetStorageManager from './BetStorageManager'
import BetValidator from './BetValidator'
import BetTypesCalculator from './BetTypesCalculator'

import { isEmpty, isString } from 'lodash'

import {
  BETSLIP_ADD_BET,
  BETSLIP_CLEAR_BETS,
  BETSLIP_CLEAR_ERROR,
  BETSLIP_REMOVE_BET,
  BETSLIP_REMOVE_BET_NOW,
  BETSLIP_SELECT_BET,
  BETSLIP_STATE_UPDATE
} from '../../actions'

import { BETSLIP_ERROR_CODE, BETSLIP_VIOLATION } from '../../error'

import { BETSLIP_VALIDATION_STATUS } from '../../constants'

import { emptyBetslipState, GlobalBetslipActions } from '../../state'

const DEFAULT_PLACE_BETS_OPTIONS = {
  id: 'main'
}
const DEFAULT_VALIDATE_OPTIONS = DEFAULT_PLACE_BETS_OPTIONS
const DEFAULT_EDIT_BET_OPTIONS = {
  id: 'edit'
}

export default class BetManager {
  constructor () {
    // Initialy main is always selected
    this.target = DEFAULT_PLACE_BETS_OPTIONS.id
    // FIXME: Temp solution; Should be working as a constructor
    this.betProcessor = new BetProcessor()
    this.validator = new BetValidator()
    this.betTypesCalculator = new BetTypesCalculator()
    this.betStorage = new BetStorageManager()

    // Betslip settings
    this.betslipSettings = new DeferredPromise()

    // Main betslip initial bet items
    this.betslipInitialOptions = new DeferredPromise()
  }

  loadBetslipSettings () {
    return SportsbookApi.getSportsbookSettings()
      .then(settings => {
        this.betslipSettings.resolve(settings)

        logger.debug('[BetManager]', 'settings resolved', settings)
      })
      .catch(error => {
        this.betslipSettings.reject(error)

        logger.debug('[BetManager]', 'unable to resolve settings', error)
      })
  }

  getBetslipSettings () {
    return this.betslipSettings
  }

  getBetslipInitialOptions () {
    // If betslip initial options resolving is already invoked
    if (this.betslipInitialOptions instanceof DeferredPromise) {
      return this.betslipInitialOptions
    }

    // Betslip resolving reset
    this.betslipInitialOptions = new DeferredPromise()

    // Resolving locally stored bet items
    this.betStorage.resolveBetItems()
      .then(betItems => {
        this.betslipInitialOptions.resolve({
          betItems,
          loading: false
        })
      })
      .catch(error => {
        this.betslipInitialOptions.reject(error)
      })
    return this.betslipInitialOptions
  }

  getOutcomeLimits (outcomeId, signal = null) {
    return BettorApi.getOutcomeLimits({ outcomeId, signal })
  }

  getSgmOdds (data, signal = null) {
    return SportsbookApi.getSgmOdds({ data, signal })
  }

  resetBetslipInitialOptions () {
    // Switching initial options to uninitialized
    this.betslipInitialOptions = undefined
  }

  focus (betslipId) {
    this.target = betslipId
  }

  isFocused () {
    // Focused if focus target is not empty string
    return (isString(this.target)) && (this.target !== '')
  }

  /*
    Adding provided bet to betslip, without autoreplacing and unselecting
  */
  addBet (payload) {
    if (isEmpty(payload) || !isBetValid(payload) || !this.isFocused()) { return }
    logger.debug('[BetManager]', 'adding new bet item', payload)

    Bus.emit(`betslip:${this.target}`, {
      type: BETSLIP_ADD_BET,
      payload
    })
  }

  /*
    Adding new bet to betslip with autoreplacing and unselecting if same bet exist
  */
  selectBet (payload) {
    if (isEmpty(payload) || !isBetValid(payload) || !this.isFocused()) { return }
    logger.debug('[BetManager]', `selecting bet item in ${this.target}`, payload)

    this.clearReports()

    Bus.emit(`betslip:${this.target}`, {
      type: BETSLIP_SELECT_BET,
      payload
    })
  }

  /*
    Removing bet from betslip
  */
  removeBet (payload) {
    if (isEmpty(payload) || !this.isFocused()) {
      return
    }

    logger.debug('[BetManager]', 'removing bet item', payload)

    Bus.emit(`betslip:${this.target}`, {
      type: BETSLIP_REMOVE_BET,
      payload
    })
  }

  removeBetNow () {
    if (!this.isFocused()) { return }
    logger.debug('[BetManager]', 'removing bet now item')

    Bus.emit(`betslip:${this.target}`, {
      type: BETSLIP_REMOVE_BET_NOW,
      payload: {}
    })
  }

  clearBets () {
    if (!this.isFocused()) { return }
    logger.debug('[BetManager]', 'clearing all bets')

    Bus.emit(`betslip:${this.target}`, {
      type: BETSLIP_CLEAR_BETS,
      payload: {}
    })

    this.betStorage.removeAllBetItems()
  }

  clearReports () {
    if (!this.isFocused()) { return }
    logger.debug('[BetManager]', 'clearing all reports')
    Bus.emit('betslip:global', {
      type: GlobalBetslipActions.BETSLIP_CLEAR_REPORTS,
      payload: { }
    })
  }

  clearErrors () {
    if (!this.isFocused()) { return }
    logger.debug('[BetManager]', 'clearing all errors')

    Bus.emit(`betslip:${this.target}`, {
      type: BETSLIP_STATE_UPDATE,
      payload: {
        errors: emptyBetslipState.errors,
        outcomesErrors: emptyBetslipState.outcomesErrors
      }
    })
  }

  clearError (errorId) {
    if (!this.isFocused() || !errorId) { return }
    logger.debug('[BetManager]', 'clearing error id:' + errorId)

    Bus.emit(`betslip:${this.target}`, {
      type: BETSLIP_CLEAR_ERROR,
      payload: errorId
    })
  }

  /*
    Server async bets validation
  */
  async validateBets (bets, options = DEFAULT_VALIDATE_OPTIONS) {
    const {
      id
    } = options
    const betslipNamespace = `betslip:${id}`
    // Clearing previous errors and enabling loading
    Bus.emit(betslipNamespace, {
      type: BETSLIP_STATE_UPDATE,
      payload: {
        errors: emptyBetslipState.errors,
        outcomesErrors: emptyBetslipState.outcomesErrors,
        loading: true,
        validationStatus: BETSLIP_VALIDATION_STATUS.VALIDATING
      }
    })

    try {
      const response = await BettorApi.validateBets({ bets })
      const bonuses = extractBonuses(response.allowedBonuses)

      // Mandatory Bonuses returned from the API
      const bonusAssignments = response.bets.reduce((bonusList, bet) => {
        const requestId = bet.requestId
        const mandatoryBonuses = bet.bonusInfoList.reduce((list, bonus) => {
          return [
            ...list,
            {
              requestId: parseInt(requestId, 10),
              bonusId: bonus.id
            }
          ]
        }, [])
        return [
          ...bonusList,
          ...mandatoryBonuses
        ]
      }, []);

      const limits = response.bets
        .filter(bet => {
          const maxBetAmount = bet?.maxBetAmount
          // 100000000 is hardcoded backend value that means no maxBetAmount is defined
          if (maxBetAmount === null || maxBetAmount === undefined || maxBetAmount === 100000000) {
            return false;
          }
          return true;
        })
        .reduce((limitsPerBet, bet) => ({
          ...limitsPerBet,
          [bet.requestId]: { maxBetAmount: bet.maxBetAmount }
        }), {});

      Bus.emit(betslipNamespace, {
        type: BETSLIP_STATE_UPDATE,
        payload: {
          loading: false,
          validationStatus: BETSLIP_VALIDATION_STATUS.VALIDATION_SUCCEEDED,
          bonuses,
          bonusAssignments,
          limits
        }
      })

      Bus.emit(Bus.events.bet.validationSuccess, response)

      return response
    } catch (error) {
      Bus.emit(Bus.events.bet.validationError, error)

      throw error
    }
  }

  /*
    Server async bets place
  */
  async placeBets (bets, options = DEFAULT_PLACE_BETS_OPTIONS) {
    const {
      id
    } = options

    const placeBetRequestId = getUID(15)

    try {
      // Broadcasting betslip placing bet event
      // starting placing bet
      Bus.emit(Bus.events.bet.placing, {
        requestId: placeBetRequestId,
        betslipId: id
      })

      // Sending request
      const response = await BettorApi.asyncPlaceBets({ bets, placeBetRequestId })

      logger.info('[BetManager]', '⏳', 'place bets submited;', `betslip=${id}`)

      // Broadcasting betslip placed bet event
      // placing bet operation registered in system
      Bus.emit(Bus.events.bet.placed, {
        requestId: placeBetRequestId,
        betslipId: id
      })

      Bus.emit(`betslip:${this.target}`, {
        type: BETSLIP_STATE_UPDATE,
        payload: {
          confirming: false
        }
      })

      return response
    } catch (error) {
      logger.info('[BetManager]', '❌', 'place bets submit failed', id, error)

      if ([BETSLIP_ERROR_CODE.BET_VALIDATION_FAILED, BETSLIP_ERROR_CODE.OUTCOME_INACTIVE].includes(error.code)) { // validate error
        Bus.emit(Bus.events.bet.placedFail,
          {
            data: {
              placeBetRequestId,
              ...error
            }
          })
      } else {
        Bus.emit(Bus.events.bet.placedFail, {
          data: {
            placeBetRequestId,
            code: error.code,
            errors: [
              {
                requestId: 'global',
                violations: [
                  {
                    code: BETSLIP_VIOLATION.INTERNAL_ERROR,
                    violationInfo: error
                  }
                ]
              }
            ]
          }
        })
      }

      throw error
    }
  }

  /*
    Server async bet limits resolve
    FIXME: Currently implemented for single bet usage
  */
  async getMaxBet (bet) {
    const {
      betsLimits
    } = await BettorApi.calculateBetsLimits({ bets: [bet] })

    return betsLimits[0]
  }

  async getBetsLimits (bets) {
    const {
      betsLimits
    } = await BettorApi.calculateBetsLimits({ bets })

    return betsLimits
  }

  async getActiveBets () {
    const {
      requests
    } = await BettorApi.getActiveRequests()

    return requests
  }

  /*
    Submit bets order
  */
  async orderBets (orders) {
    logger.debug('[BetManager]', 'submitting order bets', orders)

    // Prev implementation:
    //
    // const validBetItems = orderBetItems.filter(betItem => (
    //   Boolean(betItem.amount) && Boolean(betItem.orderOdds)
    // ))
    // const response = await OrderApi.submitOrderBet({
    //   orders: this.betProcessor.processOrderBet(validBetItems)
    // })

    try {
      const response = await OrderApi.submitOrderBet({
        orders
      })

      Bus.emit(Bus.events.bet.orderSuccess, response)

      return response
    } catch (error) {
      Bus.emit(Bus.events.bet.orderError, error)

      throw error
    }
  }

  /*
    Experimental
    Submit bet edit
  */
  async editBet (editedBet, options) {
    logger.debug('[BetManager]', 'submitting edit bet', editedBet, options)

    const { betId } = editedBet
    const { estimatedCashOut, id = DEFAULT_EDIT_BET_OPTIONS.id } = options
    const betslipNamespace = `betslip:${id}`

    Bus.emit(betslipNamespace, {
      type: BETSLIP_STATE_UPDATE,
      payload: {
        loading: true,
        errors: emptyBetslipState.errors,
        outcomesErrors: emptyBetslipState.outcomesErrors
      }
    })

    try {
      const response = await BettorApi.editBetRequest({
        editedBet,
        estimatedCashOut
      })

      Bus.emit(betslipNamespace, {
        type: BETSLIP_STATE_UPDATE,
        payload: {
          loading: false
        }
      })

      Bus.emit(Bus.events.bet.editSuccess, {
        betId,
        response
      })

      return response
    } catch (error) {
      logger.debug('[BetManager]', 'submit edit bet failed', error)

      const errors = error.errors || {
        global: {
          violations: [
            {
              code: error.code || BETSLIP_VIOLATION.INTERNAL_ERROR,
              violationInfo: error
            }
          ]
        }
      }

      Bus.emit(betslipNamespace, {
        type: BETSLIP_STATE_UPDATE,
        payload: {
          loading: false,
          errors
        }
      })

      Bus.emit(Bus.events.bet.editError, {
        betId,
        error
      })

      // Currently edit bet as cashout handling with promises
      // So we need to return as promise.reject
      throw error
    }
  }
}

function isBetValid (bet) {
  return (
    bet.eventId &&
    // bet.marketTypeId &&
    // bet.marketId &&
    bet.outcomeId
  )
}
